import { Box } from '@abyss/web/ui/Box';
import { IconBrand, IconBrandProps } from '@abyss/web/ui/IconBrand';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Link } from '@abyss/web/ui/Link';
import { Markdown } from '@abyss/web/ui/Markdown';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import { find } from 'lodash';
import React from 'react';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useLagoon } from '../../../../hooks/useLagoon';
import { MedicalVisionDrawer } from './MedicalVisionDrawer';

const InfoBox = ({
  color,
  icon,
  title,
  bullets,
  isMobile,
  testId,
}: {
  color: string;
  icon: IconBrandProps['icon'];
  title?: string;
  bullets?: string;
  isMobile: boolean;
  testId: string;
}) => (
  <Box
    color={color}
    css={{
      borderRadius: '8px',
      gap: '4px',
      display: 'flex',
      flexDirection: 'column',
    }}
    data-auto-testid={testId}
    data-testid={testId}
    padding="8px 16px 16px 16px"
  >
    <Layout.Group>
      <IconBrand icon={icon} size={48} variant="twotone" />
      <Text
        color="$primary1"
        css={{ justifyContent: 'center' }}
        fontWeight={700}
        size={isMobile ? '$sm' : '$md'}
      >
        {title}
      </Text>
    </Layout.Group>
    <Text
      color="$gray7"
      css={{ marginLeft: '4px' }}
      fontWeight={600}
      size={isMobile ? '$xs' : '$sm'}
    >
      <Markdown>{bullets}</Markdown>
    </Text>
  </Box>
);

export const MedicalVisionInfographic = ({
  isMobile = false,
}: {
  isMobile?: boolean;
}) => {
  const uiMessaging = useLagoon('ui-messaging')();
  const medicalPlanMessaging = find(uiMessaging, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE
      .MEDICAL_VS_VISION_INFOGRAPH_MEDICAL_DESCRIPTION,
  });
  const medicalPlanTitle = medicalPlanMessaging?.title;
  const medicalPlanCoversBullets = medicalPlanMessaging?.message;
  const visionPlanMessaging = find(uiMessaging, {
    key: ConstantsLagoon.MIXED_SEARCH_RESULT_PAGE
      .MEDICAL_VS_VISION_INFOGRAPH_VISION_DESCRIPTION,
  });
  const visionPlanTitle = visionPlanMessaging?.title;
  const visionPlanCoversBullets = visionPlanMessaging?.message;

  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  return (
    <React.Fragment>
      <Layout.Stack
        alignItems="left"
        alignLayout="right"
        css={{
          width: '100%',
          maxWidth: '486px',
          marginLeft: isMobile ? '0' : 'auto',
          '@screen < $md': {
            paddingRight: '12px',
            paddingLeft: '16px',
          },
        }}
        data-auto-testid="medical-vs-vision-infographic"
        data-testid="medical-vs-vision-infographic"
        grow
      >
        <Text
          color={'$primary1'}
          fontWeight={700}
          size={isMobile ? '$md' : '$lg'}
        >
          {t('MEDICAL_SPECIALISTS_VS_VISION.Whats_covered')}
        </Text>
        <Layout.Group
          css={{
            '@screen < $md': {
              paddingRight: '16px',
            },
          }}
          grow
          width="50%"
        >
          <InfoBox
            bullets={medicalPlanCoversBullets}
            color="$pastel4"
            icon="eye"
            isMobile={isMobile}
            testId="medical-info-box"
            title={medicalPlanTitle}
          />
          <InfoBox
            bullets={visionPlanCoversBullets}
            color="$pastel2"
            icon="eye_exam"
            isMobile={isMobile}
            testId="vision-info-box"
            title={visionPlanTitle}
          />
        </Layout.Group>
        <Link
          before={<IconSymbol icon="info" size={'16px'} variant="outlined" />}
          data-auto-testid="medical-vs-vision-infographic-link"
          data-testid="medical-vs-vision-infographic-link"
          onClick={() => setIsDrawerOpen(true)}
          size={'$sm'}
        >
          {t('MEDICAL_SPECIALISTS_VS_VISION.Medical_specialists_vs_vision')}
        </Link>
      </Layout.Stack>
      <MedicalVisionDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </React.Fragment>
  );
};
